import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { DataSource } from 'devextreme/common/data';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import { CreateGeoObjectInfo, SelectOtAndGidListItem } from '../../models/mapsData.model';

@Component({
    selector: 'basys-select-ot-and-gid',
    templateUrl: './select-ot-and-gid.component.html',
    styleUrls: ['./select-ot-and-gid.component.scss'],
    standalone: false
})
export class SelectOtAndGidComponent implements OnInit, OnDestroy, AfterViewInit {
  public static readonly AVOID_DUPLICATE_LIST_ENTRIES = true;
  private readonly destroy$ = new Subject<void>(); // used to cancel Observable chains when component is destroyed
  private readonly refreshToken$ = new BehaviorSubject(undefined);
  objectTypeInfoListDataSource$: Observable<DataSource>;

  currentObjectTypeInfoList: SelectOtAndGidListItem[] = [];
  ErrorMessage: string;

  isListGrouped = true;

  selectedObjectTypeName = 'false';
  actionSheetCommands: any[] = [];
  actionSheetVisible = false;
  actionSheetTarget: any = '';
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;

  @Output() closeDialog = new EventEmitter<CreateGeoObjectInfo>();

  constructor(private abcCore: AbcCore, private resourceService: ResourceManagerService) {
    this.resAsync = this.resourceService.getResourceSet('basys-map-frame');
    this.ErrorMessage = null;
  }

  async ngOnInit() {
    this.ErrorMessage = null;
    this.res = await this.resAsync;

    this.objectTypeInfoListDataSource$ = this.refreshToken$.pipe(
      tap((n) => {
        this.ErrorMessage = null;
      }),
      concatMap((n) => this.createListDataSource(this.currentObjectTypeInfoList)),
      // delay(5000), // For testing/debugging loading phase
      catchError((err) => {
        this.ErrorMessage = this.abcCore.error.getFullErrorMessage(err);
        return of(<DataSource>null);
      })
    );
  }

  ngAfterViewInit(): void {
    // this.objectTypeInfosList is NOW valid !!
  }

  ngOnDestroy(): void {
    this.ErrorMessage = null;
    this.refreshToken$.complete();
    this.destroy$.next(null); // trigger the unsubscribe
    this.destroy$.complete(); // finalize & clean up the subject stream
  }

  public initialize(objectTypeAndGeoDefIdListItems: SelectOtAndGidListItem[], refreshList = false): void {
    this.currentObjectTypeInfoList = objectTypeAndGeoDefIdListItems;

    if (refreshList) {
      this.refreshList();
    }
  }

  private refreshList(): void {
    this.refreshToken$.next(undefined);
  }

  private createListDataSource(dataItems: SelectOtAndGidListItem[]): Observable<DataSource> {
    const groupKeys: string[] = [];
    dataItems.forEach((i) => {
      if (!groupKeys.includes(i.groupObjectTypeName)) {
        groupKeys.push(i.groupObjectTypeName);
      }
    });

    this.isListGrouped = groupKeys.length > 1 || (groupKeys.length === 1 && groupKeys[0] !== null);

    const dataSource = new DataSource({
      store: dataItems,
      paginate: false, // No effect (Data source is still paginated)?
      pageSize: 100, // No effect (Data source is still paginated)?
    });

    if (this.isListGrouped) {
      dataSource.group('groupObjectTypeName');
    }

    dataSource.paginate(false); // No effect (Data source is still paginated)?

    return from(dataSource.load());
  }

  cancel(): void {
    this.closeDialog.emit(null);
  }

  listItemClick(e) {
    this.selectedObjectTypeName = (<SelectOtAndGidListItem>e.itemData).concreteObjectTypeName;
    this.actionSheetCommands = this.createActionSheetCommandList(e.itemData);
    this.actionSheetTarget = e.itemElement;
    this.actionSheetVisible = true;
  }

  createActionSheetCommandList(listItemData: any): any[] {
    let commands: any[] = [];

    if (
      listItemData instanceof SelectOtAndGidListItem &&
      listItemData.geoIdentifiers &&
      listItemData.geoIdentifiers.length > 0
    ) {
      commands = listItemData.geoIdentifiers.map((geoId) =>
        Object.create({
          text: geoId,
          type: 'default',
        })
      );

      commands.push(
        Object.create({
          text: 'Zurück',
          type: 'back',
        })
      );
    }

    return commands;
  }

  actionSheetItemClicked(e) {
    if (this.selectedObjectTypeName && e.itemData) {
      if (e.itemData.type === 'back') {
        this.cancelCurrentActionSheet();
      } else {
        this.closeDialog.emit(
          new CreateGeoObjectInfo({ objectTypeName: this.selectedObjectTypeName, geoIdentifier: e.itemData.text })
        );
      }
    }
  }

  cancelCurrentActionSheet(): void {
    this.selectedObjectTypeName = null;
    this.actionSheetCommands = [];
    this.actionSheetTarget = null;
    this.actionSheetVisible = false;
  }
}
